import { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ReactSelect from "react-select";
import FormField from "../../../components/FormField";
import Button from "../../../components/Button";
import { useUpdateBookingEngine } from "../hooks";
import {
  BookingEngineType,
  BOOKING_ENGINE_OPTIONS,
  ENGINE_URL_PLACEHOLDERS,
} from "../types";
import { InfoRow } from "./InfoRow";

type Props = {
  configId: string;
  bookingEngineBaseUrl: string;
  bookingEngineType: BookingEngineType;
  bookingEngineConfig?: Record<string, any> | null;
};

const validationSchema = Yup.object({
  bookingEngineBaseUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([/?#].*)?$/,
      "Must be a valid URL"
    )
    .required("URL is required"),
});

const ENGINE_LABELS: Record<BookingEngineType, string> = {
  MEWS: "Mews",
  CLOUDBEDS: "Cloudbeds",
  RESNEXUS: "ResNexus",
  AUTUMN: "Autumn",
  CUSTOM: "Custom",
};

export function BookingEngineCard({
  configId,
  bookingEngineBaseUrl,
  bookingEngineType,
  bookingEngineConfig,
}: Props) {
  const [editing, setEditing] = useState(false);
  const updateBookingEngine = useUpdateBookingEngine();

  if (!editing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">
            Booking Engine
          </h3>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </div>
        <div className="space-y-0">
          <InfoRow label="Engine type" value={ENGINE_LABELS[bookingEngineType] || bookingEngineType} />
          <InfoRow label="Deeplink URL" value={bookingEngineBaseUrl} />
          {bookingEngineConfig &&
            Object.keys(bookingEngineConfig).length > 0 &&
            Object.entries(bookingEngineConfig).map(([key, val]) => (
              <InfoRow key={key} label={key} value={String(val)} />
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
      <h3 className="text-base font-semibold text-gray-900 mb-3">
        Edit Booking Engine
      </h3>
      <Formik
        initialValues={{
          bookingEngineBaseUrl,
          bookingEngineType,
          bookingEngineConfig: bookingEngineConfig || {},
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          updateBookingEngine.mutate(
            {
              configId,
              data: {
                bookingEngineBaseUrl: values.bookingEngineBaseUrl,
                bookingEngineType: values.bookingEngineType,
                bookingEngineConfig:
                  Object.keys(values.bookingEngineConfig).length > 0
                    ? values.bookingEngineConfig
                    : null,
              },
            },
            { onSuccess: () => setEditing(false) }
          );
        }}
      >
        {({ values, setFieldValue }) => (
          <Form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Engine Type
              </label>
              <ReactSelect
                options={BOOKING_ENGINE_OPTIONS}
                value={BOOKING_ENGINE_OPTIONS.find(
                  (o) => o.value === values.bookingEngineType
                )}
                onChange={(opt) =>
                  setFieldValue("bookingEngineType", opt?.value)
                }
                className="text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deeplink URL
              </label>
              <input
                type="text"
                name="bookingEngineBaseUrl"
                value={values.bookingEngineBaseUrl}
                onChange={(e) => setFieldValue("bookingEngineBaseUrl", e.target.value)}
                className="w-full border border-neutral-400 rounded px-3 py-2 text-sm"
                placeholder={
                  ENGINE_URL_PLACEHOLDERS[values.bookingEngineType] ||
                  "https://..."
                }
              />
            </div>

            {values.bookingEngineType === "AUTUMN" && (
              <>
                <FormField
                  name="bookingEngineConfig.hotelCode"
                  label="Hotel Code"
                  placeholder="e.g. my-hotel"
                />
                <FormField
                  name="bookingEngineConfig.pricingApiUrl"
                  label="Pricing API URL"
                  placeholder="https://api.example.com/pricing"
                />
              </>
            )}

            <div className="flex items-center gap-2 pt-2">
              <Button
                type="submit"
                loading={updateBookingEngine.isPending}
                disabled={updateBookingEngine.isPending}
              >
                Save
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={updateBookingEngine.isPending}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

